import React from "react";
import Link from "next/link";
import If from "@/components/common/If";
import { Details } from "../Details";
import type { TopNavItem } from "@/lib/top-nav";

export function isExternalNavHref(href?: string) {
  return Boolean(href && /^(https?:)?\/\//i.test(href));
}

function shouldUseAnchorTag(href?: string) {
  return Boolean(
    href && (/^(https?:)?\/\//i.test(href) || /^(mailto:|tel:)/i.test(href)),
  );
}

function getNavItemKey(item: Pick<TopNavItem, "title" | "href">) {
  return item.href || item.title;
}

function NavItemIcon({ iconUrl }: { iconUrl?: string }) {
  if (!iconUrl) return null;

  return (
    <img
      src={iconUrl}
      alt=""
      aria-hidden="true"
      className="w-[30px] shrink-0 object-contain"
      loading="lazy"
    />
  );
}

export function NavLink({ link }: { link: TopNavItem }) {
  if (!link?.href) return null;

  const useAnchorTag = shouldUseAnchorTag(link.href);
  const isExternal = isExternalNavHref(link.href);
  const content = (
    <>
      <div className="flex items-center gap-2">
        <h2
          className="text-[1.25rem] font-hero text-current"
          dangerouslySetInnerHTML={{ __html: link.title }}
        />
        <If condition={link.iconUrl}>
          <NavItemIcon iconUrl={link.iconUrl} />
        </If>
      </div>
      <span>
        <svg
          width="15"
          viewBox="0 0 27 30"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="pointer-events-none"
        >
          <line
            x1="1.25"
            y1="0.566987"
            x2="26.5116"
            y2="15.1518"
            stroke="#EAE8E4"
          />
          <line
            x1="0.832031"
            y1="29.567"
            x2="26.0936"
            y2="14.9822"
            stroke="#EAE8E4"
          />
        </svg>
      </span>
    </>
  );

  return useAnchorTag ? (
    <li className="stack gap-2 items-start">
      <a
        href={link.href}
        className="w-full flex justify-between items-center pr-3"
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
      >
        {content}
      </a>
      <If condition={link.description}>
        <span className="text-xs font-thin text-white text-left">
          {link.description}
        </span>
      </If>
    </li>
  ) : (
    <li className="stack gap-2 items-start">
      <Link
        href={link.href}
        className="w-full flex justify-between items-center pr-3"
      >
        {content}
      </Link>
      <If condition={link.description}>
        <span className="text-xs font-thin text-white text-left">
          {link.description}
        </span>
      </If>
    </li>
  );
}

function renderSubMenu(items: TopNavItem[], listClassName: string) {
  if (!items.length) return null;

  const hasNestedItems = items.some((item) => item.items.length > 0);

  if (!hasNestedItems) {
    return (
      <ul className={listClassName}>
        {items.map((item) => (
          <NavLink key={getNavItemKey(item)} link={item} />
        ))}
      </ul>
    );
  }

  return <Details items={toDetailItems(items, listClassName)} isSub />;
}

export function toDetailItems(
  items: TopNavItem[],
  listClassName = "stack gap-4",
) {
  return items.map((item) => {
    const hasChildren = item.items.length > 0;
    const icon = item.iconUrl ? (
      <NavItemIcon iconUrl={item.iconUrl} />
    ) : undefined;

    if (!hasChildren) {
      return {
        title: item.title,
        link: item.href,
        detail: item.description,
        icon,
        external: isExternalNavHref(item.href),
      };
    }

    return {
      title: item.title,
      detail: item.description,
      icon,
      description: renderSubMenu(item.items, listClassName),
    };
  });
}
